import { createRouter } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
import type { Session, User } from "./stores/auth";

/**
 * Auth state injected into every route's `beforeLoad` via TanStack Router
 * context. Populated by `__root.tsx`'s `beforeLoad` from `authClient.getSession()`,
 * so route guards never have to read from Legend State (which is not hydrated
 * synchronously at route-resolution time).
 */
export type RouterContext = {
  session: Session["session"] | null;
  user: User | null;
};

export function getRouter() {
  const router = createRouter({
    routeTree,
    scrollRestoration: true,
    // Data is hydrated through Legend State stores, not route loaders, so
    // global router-level preload would only run beforeLoad redirects (e.g.
    // `_app/index.tsx`, `_app/route.tsx`) on hover for no benefit. Specific
    // links opt in via `preload="intent"` — notably the board sidebar links,
    // whose route `beforeLoad` makes the `resolveAccess` network call worth
    // warming on hover.
    defaultPreload: false,
    // Reuse a preloaded match (e.g. the warmed board `resolveAccess` result)
    // when the user actually clicks within this window, so opted-in
    // `preload="intent"` links don't re-run beforeLoad on click.
    defaultPreloadStaleTime: 30_000,
    context: { session: null, user: null } satisfies RouterContext,
  });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
